import {
  createSlice,
  PayloadAction,
  createAsyncThunk,
  Slice,
} from '@reduxjs/toolkit'
import { db } from 'fb/index'
import {
  collection,
  query,
  where,
  getDocs,
  orderBy,
  DocumentReference,
  doc,
  addDoc,
  updateDoc,
  getDoc,
} from 'firebase/firestore'
import { fetchAdditiveUses } from 'api/masterApi'
import { isRestMasterEnabled } from 'api/featureFlags'

interface AdditiveUseState {
  additiveUses: AdditiveUse[]
}
interface AdditiveUse {
  name: string
  is_disabled: boolean
  additive_use_ref: DocumentReference
  disabled_material_name: boolean
  disabled_use_name: boolean
}

export type AdditiveUseType = AdditiveUse

export const NOT_DISPLAY_MATERIAL_NAME_ADDITIVE_USE_LIST = [
  'イーストフード',
  'ガムベース',
  'かんすい',
  '酵素',
  '光沢剤',
  '香料',
  '酸味料',
  '調味料',
  '豆腐用凝固剤',
  '苦味料',
  '乳化剤',
  'pH調整剤',
  '膨張剤',
  '軟化剤',
]

const initialState = {
  additiveUses: [],
} as AdditiveUseState

export const getAdditiveUses = createAsyncThunk<AdditiveUse[]>(
  'additiveUse/getAdditiveUses',
  async () => {
    // Phase 3: REACT_APP_USE_REST_MASTER=true のとき daseruno-api から取得
    if (isRestMasterEnabled()) {
      const uses = await fetchAdditiveUses()
      return uses
        .filter((u) => u.firestore_document_id)
        .map((u) => ({
          name: u.label_name,
          is_disabled: u.is_disabled,
          // self-reference を firestore_document_id から再構築
          additive_use_ref: doc(db, 'additive_uses', u.firestore_document_id!),
          // MySQL の is_not_displayed_to_bread_label が Firestore の disabled_material_name に対応
          disabled_material_name: u.is_not_displayed_to_bread_label,
          // disabled_use_name は MySQL に未存在のため false で固定
          // 使用コンポーネントが存在しないため影響なし
          disabled_use_name: false,
        }))
    }

    // フォールバック: 従来の Firestore 読み取り
    const q = query(
      collection(db, 'additive_uses'),
      where('is_disabled', '==', false),
      orderBy('order'),
    )
    const querySnapshot = await getDocs(q)
    const additiveUses: AdditiveUse[] = []
    await Promise.all(
      querySnapshot.docs.map(async (document) => {
        const additiveUse = document.data()
        additiveUses.push({
          name: additiveUse.name,
          is_disabled: additiveUse.is_disabled,
          additive_use_ref: additiveUse.additive_use_ref,
          disabled_material_name: additiveUse.disabled_material_name,
          disabled_use_name: additiveUse.disabled_use_name ? true : false,
        })
      }),
    )
    return additiveUses
  },
)

export const getAdditiveUseById = createAsyncThunk<
  AdditiveUse | null,
  { id: string }
>('additiveUse/getAdditiveUseById', async ({ id }) => {
  const additiveUseQuery = doc(db, 'additive_uses', id)
  const additiveUseQuerySnapshot = await getDoc(additiveUseQuery)
  const additiveUse = additiveUseQuerySnapshot.data()
  if (!additiveUse) {
    return null
  }
  const additiveUseResponse: AdditiveUse = {
    name: additiveUse.name,
    is_disabled: additiveUse.is_disabled,
    additive_use_ref: additiveUse.additive_use_ref,
    disabled_material_name: additiveUse.disabled_material_name,
    disabled_use_name: additiveUse.disabled_use_name ? true : false,
  }
  return additiveUseResponse
})

// is_displayed_only_use_name, is_displayed_only_material_nameはロジックに含めていない
export const createAdditiveUses = createAsyncThunk<boolean>(
  'additiveUse/createAdditiveUses',
  async () => {
    const additiveUseRef = collection(db, 'additive_uses')
    const nameList = [
      '甘味料',
      '着色料',
      '保存料',
      '増粘剤',
      '安定剤',
      'ゲル化剤',
      '糊料',
      '酸化防止剤',
      '発色剤',
      '漂白剤',
      '防かび剤',
      '防ばい剤',
      '乳化剤',
      '膨張剤',
      '調味料',
      '酸味料',
      '苦味料',
      '光沢剤',
      'ガムベース',
      '栄養強化剤',
      '製造用剤等',
      'かんすい',
      '結着剤',
      '消泡剤',
      '抽出溶剤',
      '豆腐用凝固剤',
      '日持向上剤',
      '離型剤',
      'ろ過助剤',
      '香料',
      'pH調整剤',
      '結着防止剤',
      'イーストフード',
      '酵素',
      '軟化剤',
      '加工助剤',
    ]
    await Promise.all(
      nameList.map(async (name, index) => {
        const disabledMaterialName =
          NOT_DISPLAY_MATERIAL_NAME_ADDITIVE_USE_LIST.includes(name)
            ? true
            : false
        const createData = {
          name: name,
          is_disabled: false,
          order: index,
          disabled_material_name: disabledMaterialName,
        }
        const docRef: DocumentReference = await addDoc(
          additiveUseRef,
          createData,
        )
        const additiveUseDoc = doc(db, 'additive_uses', docRef.id)
        const updateData = {
          additive_use_ref: additiveUseDoc,
        }
        await updateDoc(additiveUseDoc, updateData)
      }),
    )
    return true
  },
)

export const additiveUseSlice: Slice<AdditiveUseState, {}, 'additiveUse'> =
  createSlice({
    name: 'additiveUse',
    initialState,
    reducers: {},
    extraReducers: {
      [getAdditiveUses.pending.type]: () => {},
      [getAdditiveUses.fulfilled.type]: (
        state,
        action: PayloadAction<AdditiveUse[]>,
      ) => {
        state.additiveUses = action.payload
      },
      [getAdditiveUses.rejected.type]: () => {},
    },
  })

export default additiveUseSlice.reducer
